import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IContract } from '../../../../../model/shared/IContract';
import { Button } from 'react-bootstrap';
import { ContractOverviewAccordion } from './ContractOverviewAccordion';
import ChildOfferModal from './modal';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedUser as setSelectedUserForModal } from '../../../../../store/brand/childOfferBrandSlice';
import HtmlToPdf from '../../../../../shared/components/generate-pdf';
import { Pagination } from '../../../../../shared/components/Pagination';
import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import { IAllStates } from '../../../../../store/rootReducer';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { theme } from '../../../../../provider/MaterialThemeProvider';
import { BookingService } from '../service';
import { ErrorToast } from '../../../../../utils/toasters';
import { CampaignOffersDownloadButton } from '../../../../../shared/components/pdf/DownloadBulkOfferPdf';
import { useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import { ContractListSkeleton } from './ContractsOverviewCardSkeleton';

interface IContractOverviewCardProps {
    dropdownContracts?: IContract[];
    contracts?: IContract[];
    info: any;
    setUpdateList?: React.Dispatch<React.SetStateAction<boolean>>;
    hasPdf?: boolean;
    offerFilter?: string;
    totalPages?: number;
    filterContracts?: { page: number; perPage: number; userIds: any };
    handlePagination: (updatePage: number) => void;
    contractCount: number;
    setFilterContracts: React.Dispatch<React.SetStateAction<{ page: number; perPage: number; userIds: any }>>;
    isLoading?: boolean;
}

function ContractsOverviewCard({
    contracts,
    contractCount,
    info,
    setUpdateList,
    offerFilter,
    hasPdf,
    totalPages,
    filterContracts,
    handlePagination,
    setFilterContracts,
    dropdownContracts,
    isLoading = false,
}: IContractOverviewCardProps) {
    const [showModal, setShowModal] = useState(false);
    const { id } = useParams<keyof IParams>();
    const { campaign } = useSelector((states: IAllStates) => states.selectedCampaign);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const openOfferModal = () => {
        if (!contracts) {
            setShowModal(true);
            return;
        }
        BookingService.getContractUsers(String(campaign?.id), {
            otherKeys: {
                page: 1,
                perPage: 0,
            },
        })
            .then(response => {
                const selectedUser = response.data.find(item => item.id === +contracts[0].user?.id);
                dispatch(setSelectedUserForModal(selectedUser));
                setShowModal(true);
            })
            .catch(err => ErrorToast(err));
    };

    const multipleSelectHandler = (paramKey: string, paramValue: any) => {
        setFilterContracts(prevState => ({
            ...prevState,
            [paramKey]: !!`${paramValue}`?.length ? paramValue : null,
            page: 1,
        }));
    };

    return (
        <>
            <ChildOfferModal closeModal={() => setShowModal(false)} show={showModal} setUpdateList={setUpdateList} />
            <CardLayout classCard="position-relative mb-2">
                <div className="text-dark font-weight-semi-bold mb-3">
                    <h5>{t('brand.campaign.manage.filterByInfluencer')}:</h5>
                    <CustomAutocomplete
                        dataArray={dropdownContracts ?? []}
                        isWithImage={true}
                        isMultiple={true}
                        queryFilterKey="userIds"
                        changeEventKey="displayName"
                        inputLabel="user.displayName"
                        photoLabel="user.profilePhoto"
                        handleChangeParams={multipleSelectHandler}
                        inputLabelTranslation={t('brand.campaign.manage.influencerName')}
                        me={true}
                        isDisabled={isLoading}
                    />
                </div>

                <div className="default-radius d-flex" style={{ backgroundColor: '#F3F6F9', padding: '4px 8px' }}>
                    <div className="d-flex flex-column align-items-center text-center" style={{ flex: 1 }}>
                        <span className="text-primary font-14">{contractCount}</span>
                        <span className="font-14" style={{ color: theme.palette.primary.dark }}>
                            {t('brand.campaign.book.contract.overview_card.influencers')}
                        </span>
                    </div>
                    <div className="d-flex flex-column align-items-center text-center" style={{ flex: 1 }}>
                        <span className="text-primary font-14">{info?.bookedPosts - info?.usedPosts || 0}</span>
                        <span className="font-14" style={{ color: theme.palette.primary.dark }}>
                            {t('brand.campaign.book.contract.overview_card.available_postings')}
                        </span>
                    </div>
                </div>

                <div className="my-2">
                    {isLoading ? (
                        <ContractListSkeleton />
                    ) : (
                        contracts?.map(contract => <ContractOverviewAccordion key={contract.id} contract={contract} />)
                    )}
                </div>

                <div className="d-flex justify-content-end pb-lg-0 mb-lg-0">
                    <Pagination
                        page={filterContracts?.page ?? 1}
                        totalPages={totalPages ?? 0}
                        handlePagination={handlePagination}
                    />
                </div>

                {!!contracts?.length && (
                    <TooltipOverlay
                        className="w-100"
                        content={
                            campaign?.approvalStatus !== 'approved' ? (
                                <>{t(`brand.campaign.book.contract.spending_button_campaign`)}</>
                            ) : null
                        }
                    >
                        <Button
                            variant="outline-primary"
                            className="w-100"
                            onClick={openOfferModal}
                            disabled={campaign?.approvalStatus !== 'approved'}
                        >
                            {t('brand.campaign.book.contract.spending_button')}
                        </Button>
                    </TooltipOverlay>
                )}

                {/*{offerFilter === 'booked' && hasPdf && <HtmlToPdf styleContent="mt-2 w-100" styleBtn="btn-primary" />}*/}
                {offerFilter === 'booked' && hasPdf && (
                    <CampaignOffersDownloadButton
                        campaignId={id ? Number(id) : undefined}
                        className={'mt-2 w-100 btn-outline-primary'}
                    />
                )}
            </CardLayout>
        </>
    );
}

export default ContractsOverviewCard;
